import { useEffect, useState } from 'react';
import { getReportsSummary } from '../../api/originatorApi';
import { ReportTile } from '../../components/reports/ReportTile';
import { ErrorBanner } from '../../components/ui/ErrorBanner';

// Maps backend tile_key → dashboard route + supporting copy. Tiles are
// navigation-only; Run/Schedule/Export live inside each dashboard.
const TILE_META = {
  boarding_summary: {
    to: '/reports/boarding',
    title: 'Boarding summary',
    body: 'Packages arrived, stuck stages, cure SLAs at risk.',
  },
  authority_outcomes: {
    to: '/reports/authority',
    title: 'Authority outcomes',
    body: 'Admitted, repository-only, rejected, awaiting custodian review.',
  },
  facility_status: {
    to: '/reports/custody',
    title: 'Custody / facility status',
    body: 'Vault, pledged, release pending, facility-tied positions.',
  },
  spv_sold_servicing: {
    to: '/reports/spv-servicing',
    title: 'SPV / sold with servicing',
    body: 'Loans visible after sale because servicing is retained.',
  },
  evidence_export_history: {
    to: '/reports/evidence',
    title: 'Evidence & export history',
    body: 'Bundles, receipts, certified copies, report exports.',
  },
};

const HIDDEN_TILE_KEYS = new Set([
  'authority_outcomes',
  'facility_status',
  'spv_sold_servicing',
]);

export function ReportsHubPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await getReportsSummary();
        if (!cancelled) setItems(response.items || []);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load reports.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const tiles = items
    .map((item) => ({ ...TILE_META[item.tile_key], ...item }))
    .filter((tile) => tile.to && !HIDDEN_TILE_KEYS.has(tile.tile_key));

  return (
    <div className="space-y-5">
      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((t) => t + 1)} /> : null}

      {loading ? (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <li key={i} className="h-48 animate-pulse rounded-2xl border border-eorig-border bg-eorig-surface" />
          ))}
        </ul>
      ) : tiles.length ? (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map((tile) => (
            <li key={tile.tile_key}>
              <ReportTile
                to={tile.to}
                title={tile.title}
                body={tile.body}
                updatedAt={tile.updated_at}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded-2xl border border-eorig-border bg-white p-6 text-sm text-eorig-muted shadow-eorig-card">
          No report modules available.
        </p>
      )}
    </div>
  );
}
